import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Images } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryCarousel3D = ({ albums }) => {
  const navigate = useNavigate();
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next');
  
  // Use ref to track current indices for interval
  const stateRef = useRef({ currentAlbumIndex: 0, currentImageIndex: 0 });

  // Filter albums with images
  const albumsWithImages = useMemo(() => {
    return albums.filter(a => a.images && a.images.length > 0);
  }, [albums]);

  const hasAlbums = albumsWithImages.length > 0;
  const currentAlbum = hasAlbums ? albumsWithImages[currentAlbumIndex] : null;
  const currentImages = currentAlbum?.images || [];
  
  // Update ref when state changes
  useEffect(() => {
    stateRef.current = { currentAlbumIndex, currentImageIndex };
  }, [currentAlbumIndex, currentImageIndex]);
  
  // Get image URL
  const getImageUrl = useCallback((image) => {
    if (!image) return null;
    if (image.url) return image.url;
    if (image.filename) return image.url || `${BACKEND_URL}/api/uploads/gallery/${image.filename}`;
    return null;
  }, []);

  // Navigate to next image within album, or next album
  const goToNext = useCallback(() => {
    if (!hasAlbums || isAnimating) return;
    
    setIsAnimating(true);
    setSlideDirection('next');
    
    setTimeout(() => {
      const { currentAlbumIndex: albIdx, currentImageIndex: imgIdx } = stateRef.current;
      const currentAlb = albumsWithImages[albIdx];
      const imgs = currentAlb?.images || [];
      
      if (imgIdx < imgs.length - 1) {
        setCurrentImageIndex(imgIdx + 1);
      } else {
        // Next album
        const nextAlbumIdx = (albIdx + 1) % albumsWithImages.length;
        setCurrentAlbumIndex(nextAlbumIdx);
        setCurrentImageIndex(0);
      }
      
      setTimeout(() => setIsAnimating(false), 100);
    }, 300);
  }, [hasAlbums, albumsWithImages, isAnimating]);

  // Navigate to previous
  const goToPrev = useCallback(() => {
    if (!hasAlbums || isAnimating) return;
    
    setIsAnimating(true);
    setSlideDirection('prev');
    
    setTimeout(() => {
      const { currentAlbumIndex: albIdx, currentImageIndex: imgIdx } = stateRef.current;
      
      if (imgIdx > 0) {
        setCurrentImageIndex(imgIdx - 1);
      } else {
        // Previous album
        const prevAlbumIndex = albIdx === 0 ? albumsWithImages.length - 1 : albIdx - 1;
        const prevAlbumImages = albumsWithImages[prevAlbumIndex]?.images || [];
        setCurrentAlbumIndex(prevAlbumIndex);
        setCurrentImageIndex(Math.max(0, prevAlbumImages.length - 1));
      }
      
      setTimeout(() => setIsAnimating(false), 100);
    }, 300);
  }, [hasAlbums, albumsWithImages, isAnimating]);

  // Auto-rotate every 4 seconds - using simple interval without goToNext in deps
  useEffect(() => {
    if (!hasAlbums || isPaused) return;
    
    const interval = setInterval(() => {
      if (stateRef.current) {
        setIsAnimating(true);
        setSlideDirection('next');
        
        setTimeout(() => {
          const { currentAlbumIndex: albIdx, currentImageIndex: imgIdx } = stateRef.current;
          const currentAlb = albumsWithImages[albIdx];
          const imgs = currentAlb?.images || [];
          
          if (imgIdx < imgs.length - 1) {
            setCurrentImageIndex(imgIdx + 1);
          } else {
            const nextAlbumIdx = (albIdx + 1) % albumsWithImages.length;
            setCurrentAlbumIndex(nextAlbumIdx);
            setCurrentImageIndex(0);
          }
          
          setTimeout(() => setIsAnimating(false), 100);
        }, 300);
      }
    }, 4000);
    
    return () => clearInterval(interval);
  }, [hasAlbums, isPaused, albumsWithImages]);

  // Handle album click - navigate to gallery
  const handleAlbumClick = useCallback(() => {
    navigate('/galleri');
  }, [navigate]);

  // Early return AFTER all hooks
  if (!hasAlbums) return null;

  const currentImage = currentImages[currentImageIndex];
  
  // Get total images count across all albums
  const getTotalInfo = () => {
    let total = 0;
    let currentPos = 0;
    for (let i = 0; i < albumsWithImages.length; i++) {
      const albumImages = albumsWithImages[i]?.images || [];
      if (i < currentAlbumIndex) {
        currentPos += albumImages.length;
      } else if (i === currentAlbumIndex) {
        currentPos += currentImageIndex + 1;
      }
      total += albumImages.length;
    }
    return { current: currentPos, total };
  };
  
  const { current: currentPos, total: totalImages } = getTotalInfo();

  return (
    <div 
      className="relative w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="gallery-carousel-3d"
    >
      {/* 3D Carousel Container */}
      <div 
        className="relative w-full cursor-pointer group"
        onClick={handleAlbumClick}
        style={{ perspective: '1000px' }}
      >
        {/* Image wrapper with 3D rotation effect */}
        <div 
          className="relative w-full aspect-[16/9] sm:aspect-[2/1] rounded-xl overflow-hidden"
          style={{
            boxShadow: '0 20px 50px -15px rgba(0,0,0,0.6)',
            transformStyle: 'preserve-3d'
          }}
        >
          {currentImage && (
            <div
              className="absolute inset-0 transition-all duration-500 ease-out"
              style={{
                transform: isAnimating 
                  ? slideDirection === 'next'
                    ? 'rotateY(-15deg) scale(0.95) translateX(-5%)'
                    : 'rotateY(15deg) scale(0.95) translateX(5%)'
                  : 'rotateY(0deg) scale(1) translateX(0)',
                opacity: isAnimating ? 0.7 : 1,
                transformOrigin: slideDirection === 'next' ? 'right center' : 'left center'
              }}
            >
              <img
                src={getImageUrl(currentImage)}
                alt={currentAlbum?.title || ''}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
          
          {/* Album info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 pointer-events-none">
            <div className="flex items-center gap-2 mb-1">
              <Images size={14} className="text-blue-400" />
              <span className="text-blue-400 text-xs font-['Oswald'] uppercase tracking-wider">
                Album
              </span>
            </div>
            <h3 className="text-white font-['Oswald'] text-lg sm:text-xl font-bold">
              {currentAlbum.title}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm mt-1">
              Billede {currentPos} af {totalImages} · Klik for at se album
            </p>
          </div>
          
          {/* Hover effect */}
          <div className="absolute inset-0 bg-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          
          {/* Auto-rotation indicator */}
          {!isPaused && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-white text-xs font-['Oswald'] uppercase tracking-wider">Auto</span>
            </div>
          )}
        </div>
        
        {/* Navigation Arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); goToPrev(); }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 hover:scale-110 transition-all"
          data-testid="carousel-prev-btn"
        >
          <ChevronLeft size={20} />
        </button>
        
        <button
          onClick={(e) => { e.stopPropagation(); goToNext(); }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 hover:scale-110 transition-all"
          data-testid="carousel-next-btn"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      
      {/* Navigation indicators */}
      <div className="flex flex-col items-center mt-4 gap-3">
        {/* Progress bar */}
        <div className="w-full max-w-xs h-1 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${(currentPos / totalImages) * 100}%` }}
          />
        </div>
        
        {/* Browse hint */}
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <ChevronLeft size={12} />
          <span className="font-['Oswald'] uppercase tracking-wider">Blad igennem billederne</span>
          <ChevronRight size={12} />
        </div>
        
        {/* Album indicators */}
        {albumsWithImages.length > 1 && (
          <div className="flex items-center gap-2">
            {albumsWithImages.map((album, idx) => (
              <button
                key={album.album_id}
                onClick={() => {
                  setCurrentAlbumIndex(idx);
                  setCurrentImageIndex(0);
                }}
                className={`transition-all ${
                  idx === currentAlbumIndex 
                    ? 'w-6 h-1.5 bg-blue-500 rounded-full' 
                    : 'w-1.5 h-1.5 bg-gray-600 hover:bg-gray-500 rounded-full'
                }`}
                title={album.title}
                data-testid={`album-indicator-${idx}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryCarousel3D;
